import {VendorList} from "./model/vendor-list";
import {TCString} from "./tcstring/t-c-string";
import {AddtlConsent} from "./addtl-consent/addtl-consent";
import {CustomConsent} from "./custom-consent/custom-consent";

interface ISelectable {
    [key:number]:boolean|undefined;
}

interface IUserSelection {
    purposes:ISelectable;
    purposesLI:ISelectable;
    specialFeatures:ISelectable;
    vendors:ISelectable;
    vendorsLI:ISelectable;
    additionalVendors:ISelectable;
}

class UserSelection implements IUserSelection {

    private readonly _purposes: ISelectable;
    private readonly _purposesLI: ISelectable;
    private readonly _specialFeatures: ISelectable;
    private readonly _vendors: ISelectable;
    private readonly _vendorsLI: ISelectable;
    private readonly _additionalVendors: ISelectable;
    private readonly _customVendors: ISelectable;
    private _vendorList:VendorList|undefined;

    public constructor() {
        this._purposes = {};
        this._purposesLI = {};
        this._specialFeatures = {};
        this._vendors = {};
        this._vendorsLI = {};
        this._additionalVendors = {};
        this._customVendors = {};
    }

    public load(vendorList: VendorList, tcString: TCString, addtlConsent: AddtlConsent, customConsent?: CustomConsent) : void {

        this._vendorList = vendorList;

        // Datos de la vendorList
        vendorList.purposesIds().forEach((purposeId:number) => {this._purposes[purposeId] = undefined;});
        vendorList.specialFeaturesIds().forEach((featureId:number) => {this._specialFeatures[featureId] = undefined;});
        vendorList.purposesLIIds().forEach((purposeId:number) => {this._purposesLI[purposeId] = undefined;});
        vendorList.vendorIds().forEach((vendorId:number) => {this._vendors[vendorId] = undefined;});
        vendorList.vendorLIIds().forEach((vendorId:number) => {this._vendorsLI[vendorId] = undefined;});
        vendorList.additionalVendorsIds().forEach((vendorId:number) => {this._additionalVendors[vendorId] = undefined;});

        if (customConsent) {
            vendorList.customVendorsIds().forEach(vendorId => {
                this._customVendors[vendorId] = customConsent.vendors()[vendorId];
            });
        }

        if (tcString) {
            // Si hay TcString, por defecto, deseleccionamos lo que por defecto es TRUE
            vendorList.purposesLIIds().forEach((purposeId:number) => {this._purposesLI[purposeId] = false;});
            vendorList.vendorIds().forEach((vendorId:number) => {this._vendors[vendorId] = false;});
            vendorList.vendorLIIds().forEach((vendorId:number) => {this._vendorsLI[vendorId] = false;});

            // Seleccionar purposes de TcString
            if (tcString.purposes) {
                vendorList.purposesIds().forEach((purposeId: number) => {
                    const value: boolean | undefined = tcString.purposes[purposeId];
                    if (value !== undefined) this._purposes[purposeId] = value;
                });
            }

            // Seleccionar purposesLI de TcString
            if (tcString.purposesLI) {
                vendorList.purposesLIIds().forEach((purposeId: number) => {
                    const value: boolean | undefined = tcString.purposesLI[purposeId];
                    if (value !== undefined) this._purposesLI[purposeId] = value;
                });
            }

            // Seleccionar specialFeatures de TcString
            if (tcString.specialFeatures) {
                vendorList.specialFeaturesIds().forEach((featureId: number) => {
                    const value: boolean | undefined = tcString.specialFeatures[featureId];
                    if (value !== undefined) this._specialFeatures[featureId] = value;
                });
            }
            // Seleccionar vendors de TcString
            if (tcString.vendors) {
                vendorList.vendorIds().forEach((vendorId: number) => {
                    const value: boolean | undefined = tcString.vendors[vendorId];
                    if (value === true) this._vendors[vendorId] = true;
                    else if (value === false) this._vendors[vendorId] = false;
                });
            }

            // Seleccionar vendors de LI de TcString
            if (tcString.vendorsLI) {
                vendorList.vendorLIIds().forEach((vendorId: number) => {
                    const value: boolean | undefined = tcString.vendorsLI[vendorId];
                    if (value !== undefined) this._vendorsLI[vendorId] = value;
                });
            }
        }

        // Recuperamos el valor de la cookie de consentimiento adicional
        if (addtlConsent) {
            addtlConsent.acceptedVendorsIds().forEach(vendorId => {
                if (vendorList.additionalVendorsIds().includes(vendorId)) this._additionalVendors[vendorId] = true;
            });
            addtlConsent.rejectedVendorsIds().forEach(vendorId => {
                if (vendorList.additionalVendorsIds().includes(vendorId)) this._additionalVendors[vendorId] = false;
            });
        }
    }

    /**
     * Selecciona o deselecciona un propósito.
     * @param purposeId Id de el propósito.
     * @param value True para seleccionar, False para deseleccionar.
     */
    public setPurpose(purposeId:number, value:boolean) : void {
        this._purposes[purposeId] = value;
    }

    /**
     * Selecciona o deselecciona un propósito de interés legítimo.
     * @param purposeId Id de el propósito.
     * @param value True para seleccionar, False para deseleccionar.
     */
    public setLIPurpose(purposeId: number, value:boolean) : void {
        this._purposesLI[purposeId] = value;
    }

    /**
     * Selecciona o deselecciona una característica especial.
     * @param featureId Id de la característica especial.
     * @param value True para seleccionar, False para deseleccionar.
     */
    public setSpecialFeature(featureId:number, value:boolean) : void {
        this._specialFeatures[featureId] = value;
    }

    /**
     * Selecciona o deselecciona un vendor.
     * @param vendorId Id de el vendor.
     * @param value True para seleccionar, False para deseleccionar.
     */
    public setVendor(vendorId:number, value:boolean) : void
    {
        this._vendors[vendorId] = value;
    }

    /**
     * Selecciona o deselecciona un vendor de interés legítimo.
     * @param vendorId Id de el vendor.
     * @param value True para seleccionar, False para deseleccionar.
     */
    public setVendorLI(vendorId:number, value:boolean) : void {
        this._vendorsLI[vendorId] = value;
    }

    /**
     * Selecciona o deselecciona un vendor adicional externo.
     * @param vendorId Id de el vendor.
     * @param value True para seleccionar, False para deseleccionar.
     */
    public setAdditionalVendor(vendorId:number, value:boolean) : void {
        this._additionalVendors[vendorId] = value;
    }

    /**
     * Selecciona o deselecciona un vendor personalizado.
     * @param vendorId Id de el vendor.
     * @param value True para seleccionar, False para deseleccionar.
     */
    public setCustomVendor(vendorId: number, value: boolean) : void {
        this._customVendors[vendorId] = value;
    }

    /**
     * Acepta todos los propósitos y características especiales.
     * No se guarda el estado, sólo la selección provisional
     * del usuario.
     * @param value True para seleccionar, False para deseleccionar.
     */
    public setAllPurposesAndSpecialFeatures(value:boolean) : void {
        // Propósitos
        value ? this.acceptAllPurposes() : this.rejectAllPurposes();

        // Características especiales
        value ? this.acceptAllSpecialFeatures() : this.rejectAllSpecialFeatures();
    }

    /**
     * Acepta todos los propósitos de LI.
     * No se guarda el estado, sólo la selección provisional
     * del usuario.
     * @param value True para seleccionar, False para deseleccionar.
     */
    public setAllPurposesLI(value:boolean) : void {
        // Propósitos de LI
        value ? this.acceptAllPurposesLI() : this.rejectAllPurposesLI();
    }

    /**
     * Acepta todos los vendors, tanto normales como de LI.
     * No se guarda el estado, sólo la selección provisional
     * del usuario.
     * @param value True para seleccionar, False para deseleccionar.
     */
    public setAllVendors(value:boolean) : void {
        // Vendors
        value ? this.acceptAllVendors() : this.rejectAllVendors();

        // Vendors de LI
        value ? this.acceptAllVendorsLI() : this.rejectAllVendorsLI();

        // Vendors adicionales
        value ? this.acceptAllAdditionalVendors() : this.rejectAllAdditionalVendors();

        // Custom vendors
        value ? this.acceptAllCustomVendors() : this.rejectAllCustomVendors();
    }

    public get specialFeatures() : ISelectable {
        return this._specialFeatures;
    }

    public get purposes() : ISelectable {
        return this._purposes;
    }

    public get purposesLI() : ISelectable {
        return this._purposesLI;
    }

    public get vendors() : ISelectable {
        return this._vendors;
    }

    public get vendorsLI() : ISelectable {
        return this._vendorsLI;
    }

    public get additionalVendors() : ISelectable {
        return this._additionalVendors;
    }

    /**
     * Selecciona como aceptados todos los consentimientos.
     */
    public acceptAll() : void {
        // Propósitos
        this._vendorList?.purposesIds().forEach((purposeId:number) => {this._purposes[purposeId] = true;});

        // Propósitos LI
        this._vendorList?.purposesLIIds().forEach((purposeId:number) => {this._purposesLI[purposeId] = true;});

        // Características especiales
        this._vendorList?.specialFeaturesIds().forEach((featureId:number) => {this._specialFeatures[featureId] = true;});

        // Vendors
        this._vendorList?.vendorIds().forEach((vendorId:number) => {this._vendors[vendorId] = true;});

        // Vendors LI
        this._vendorList?.vendorLIIds().forEach((vendorId:number) => {this._vendorsLI[vendorId] = true;});

        // Vendors adicionales
        this._vendorList?.additionalVendorsIds().forEach((vendorId:number) => {this._additionalVendors[vendorId] = true;});

        // Custom vendors
        this._vendorList?.customVendorsIds().forEach((vendorId:number) => {this._customVendors[vendorId] = true;});
    }

    /**
     * Establece como rechazados todos los consentimientos.
     */
    public rejectAll() : void {
        // Propósitos
        this._vendorList?.purposesIds().forEach((purposeId:number) => {this._purposes[purposeId] = false;});

        // Propósitos LI
        this._vendorList?.purposesLIIds().forEach((purposeId:number) => {this._purposesLI[purposeId] = false;});

        // Características especiales
        this._vendorList?.specialFeaturesIds().forEach((featureId:number) => {this._specialFeatures[featureId] = false;});

        // Vendors
        this._vendorList?.vendorIds().forEach((vendorId:number) => {this._vendors[vendorId] = false;});

        // Vendors LI
        this._vendorList?.vendorLIIds().forEach((vendorId:number) => {this._vendorsLI[vendorId] = false;});

        // Vendors adicionales
        this._vendorList?.additionalVendorsIds().forEach((vendorId:number) => {this._additionalVendors[vendorId] = false;});

        // Custom vendors
        this._vendorList?.customVendorsIds().forEach((vendorId:number) => {this._customVendors[vendorId] = false;});
    }

    /**
     * Devuelve los Ids de los propósitos aceptados.
     */
    public acceptedPurposes() : number[] {
        return Object.keys(this._purposes)
            .filter(purposeIdStr => this._purposes[parseInt(purposeIdStr)] === true)
            .map(purposeIdStr => parseInt(purposeIdStr))
        ;
    }

    /**
     * Devuelve los Ids de los propósitos rechazados.
     */
    public rejectedPurposes(): number[] {
        return Object.keys(this._purposes)
            .filter(purposeIdStr => this._purposes[parseInt(purposeIdStr)] === false)
            .map(purposeIdStr => parseInt(purposeIdStr))
        ;
    }

    /**
     * Devuelve los Ids de las características especiales aceptadas.
     */
    public acceptedSpecialFeatures() : number[] {
        return Object.keys(this._specialFeatures)
            .filter(featureIdStr => this._specialFeatures[parseInt(featureIdStr)] === true)
            .map(featureIdStr => parseInt(featureIdStr))
        ;
    }

    /**
     * Devuelve los Ids de las características especiales rechazadas.
     */
    public rejectedSpecialFeatures() : number[] {
        return Object.keys(this._specialFeatures)
            .filter(featureIdStr => this._specialFeatures[parseInt(featureIdStr)] === false)
            .map(featureIdStr => parseInt(featureIdStr))
        ;
    }

    /**
     * Devuelve los Ids de los propósitos de LI aceptados.
     */
    public acceptedPurposesLI() : number[] {
        return Object.keys(this._purposesLI)
            .filter(purposeIdStr => this._purposesLI[parseInt(purposeIdStr)] === true)
            .map(purposeIdStr => parseInt(purposeIdStr))
        ;
    }

    /**
     * Devuelve los Ids de los propósitos de LI rechazados.
     */
    public rejectedPurposesLI() : number[] {
        return Object.keys(this._purposesLI)
            .filter(purposeIdStr => this._purposesLI[parseInt(purposeIdStr)] === false)
            .map(purposeIdStr => parseInt(purposeIdStr))
        ;
    }

    /**
     * Devuelve los Ids de vendors aceptados.
     */
    public acceptedVendorsIds() : number[] {
        return Object.keys(this._vendors)
            .filter(vendorIdStr => this._vendors[parseInt(vendorIdStr)] === true)
            .map(vendorIdStr => parseInt(vendorIdStr))
        ;
    }

    /**
     * Devuelve los Ids de vendors rechazados.
     */
    public rejectedVendorsIds() : number[] {
        return Object.keys(this._vendors)
            .filter(vendorIdStr => this._vendors[parseInt(vendorIdStr)] === false)
            .map(vendorIdStr => parseInt(vendorIdStr))
        ;
    }

    /**
     * Devuelve el mayor Id de vendor aceptado.
     */
    public maxVendorAccepted() : number {
        return Math.max(...this.acceptedVendorsIds(), 0);
    }

    /**
     * Devuelve los Ids de vendors de LI aceptados.
     */
    public acceptedVendorsLIIds() : number[] {
        return Object.keys(this._vendorsLI)
            .filter(vendorIdStr => this._vendorsLI[parseInt(vendorIdStr)] === true)
            .map(vendorIdStr => parseInt(vendorIdStr))
        ;
    }

    /**
     * Devuelve los Ids de vendors de LI rechazados.
     */
    public rejectedVendorsLIIds() : number[] {
        return Object.keys(this._vendorsLI)
            .filter(vendorIdStr => this._vendorsLI[parseInt(vendorIdStr)] === false)
            .map(vendorIdStr => parseInt(vendorIdStr))
        ;
    }

    /**
     * Devuelve el mayor Id de vendor de LI aceptado.
     */
    public maxVendorLIAccepted() : number {
        return Math.max(...this.acceptedVendorsLIIds(), 0);
    }

    /**
     * Devuelve los Ids de vendors adicionales aceptados.
     */
    public acceptedAdditionalVendorsIds() : number[] {
        return Object.keys(this._additionalVendors).filter((vendorIdStr:string) => {
            return this._additionalVendors[parseInt(vendorIdStr)];
        }).map((vendorIdStr:string) => {
            return parseInt(vendorIdStr);
        });
    }

    /**
     * Devuelve los Ids de vendors adicionales rechazados.
     */
    public rejectedAdditionalVendorsIds(): number[] {
        return Object.keys(this._additionalVendors)
            .filter(vendorIdStr => this._additionalVendors[parseInt(vendorIdStr)] === false)
            .map(vendorIdStr => parseInt(vendorIdStr))
        ;
    }

    /**
     * Devuelve los Ids de vendors personalizados aceptados.
     */
    public acceptedCustomVendorsIds() : number[] {
        return Object.keys(this._customVendors).filter((vendorIdStr:string) => {
            return this._customVendors[parseInt(vendorIdStr)];
        }).map((vendorIdStr:string) => {
            return parseInt(vendorIdStr);
        });
    }

    /**
     * Acepta de manera provisional todos los propósitos.
     */
    public acceptAllPurposes() : void {
        this._vendorList?.purposesIds().forEach((purposeId:number) => {this._purposes[purposeId] = true;});
    }

    /**
     * Rechaza de manera provisional todos los propósitos.
     */
    public rejectAllPurposes() : void {
        this._vendorList?.purposesIds().forEach((purposeId:number) => {this._purposes[purposeId] = false;});
    }

    /**
     * Acepta de manera provisional todas las características especiales.
     */
    public acceptAllSpecialFeatures() : void {
        this._vendorList?.specialFeaturesIds().forEach((featureId:number) => {this._specialFeatures[featureId] = true;});
    }

    /**
     * Rechaza de manera provisional todas las características especiales.
     */
    public rejectAllSpecialFeatures() : void {
        this._vendorList?.specialFeaturesIds().forEach((featureId:number) => {this._specialFeatures[featureId] = false;});
    }

    /**
     * Acepta de manera provisional todos los propósitos de LI.
     */
    public acceptAllPurposesLI() : void {
        this._vendorList?.purposesLIIds().forEach((purposeId:number) => {this._purposesLI[purposeId] = true;});
    }

    /**
     * Rechaza de manera provisional todos los propósitos de LI.
     */
    public rejectAllPurposesLI() : void {
        this._vendorList?.purposesLIIds().forEach((purposeId:number) => {this._purposesLI[purposeId] = false;});
    }

    /**
     * Acepta de manera provisional todos los vendors.
     */
    public acceptAllVendors() : void {
        this._vendorList?.vendorIds().forEach((vendorId:number) => {this._vendors[vendorId] = true;});
    }

    /**
     * Rechaza de manera provisional todos los vendors.
     */
    public rejectAllVendors() : void {
        this._vendorList?.vendorIds().forEach((vendorId:number) => {this._vendors[vendorId] = false;});
    }

    /**
     * Acepta de manera provisional todos los vendors de LI.
     */
    public acceptAllVendorsLI() : void {
        this._vendorList?.vendorLIIds().forEach((vendorId:number) => {this._vendorsLI[vendorId] = true;});
    }

    /**
     * Rechaza de manera provisional todos los vendors de LI.
     */
    public rejectAllVendorsLI() : void {
        this._vendorList?.vendorLIIds().forEach((vendorId:number) => {this._vendorsLI[vendorId] = false;});
    }

    /**
     * Acepta de manera provisional todos los vendors adicionales.
     */
    public acceptAllAdditionalVendors() : void {
        this._vendorList?.additionalVendorsIds().forEach((vendorId:number) => {this._additionalVendors[vendorId] = true;});
    }

    /**
     * Rechaza de manera provisional todos los vendors adicionales.
     */
    public rejectAllAdditionalVendors() : void {
        this._vendorList?.additionalVendorsIds().forEach((vendorId:number) => {this._additionalVendors[vendorId] = false;});
    }

    /**
     * Acepta de manera provisional todos los vendors custom.
     */
    public acceptAllCustomVendors() : void {
        this._vendorList?.customVendorsIds().forEach((vendorId:number) => {this._customVendors[vendorId] = true;});
    }

    /**
     * Rechaza de manera provisional todos los vendors custom.
     */
    public rejectAllCustomVendors() : void {
        this._vendorList?.customVendorsIds().forEach((vendorId:number) => {this._customVendors[vendorId] = false;});
    }
}

export {UserSelection}
